import { useState } from 'react';
import useSWR from 'swr';

import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';
import { fetchForkStatus, IForkStatusResponse, TForkStatus } from '@/lib/repositoryForkApi';
import { IRepository } from '@/types';
const SHORT_POLLING_INTERVAL = 1 * 1000; // 1 second
const LONG_POLLING_INTERVAL = 10 * 1000; // 10 seconds

interface useRepositoryForkStatusParams {
  repository: IRepository;
  initialData?: IForkStatusResponse | undefined;
}

const useRepositoryForkStatus = ({ repository, initialData }: useRepositoryForkStatusParams) => {
  const [status, setStatus] = useState<string>(initialData?.fork_status.status || 'pending');

  if (!repository.forked_from) {
    return {
      isLoading: false,
      isValidating: false,
      error: undefined,
      status: null,
      revalidate: () => {},
    };
  }
  if (initialData && initialData.fork_status.status === 'complete') {
    return {
      isLoading: false,
      isValidating: false,
      error: undefined,
      status: initialData.fork_status,
      revalidate: () => {},
    };
  }

  const { namespace, name: repositoryName } = repository;
  const fetchKey = ['fetch_fork_status', namespace, repositoryName];

  const fetcher = async () => {
    const result = await fetchForkStatus(namespace, repositoryName);
    setStatus(result.fork_status.status);
    console.log('result', result.fork_status.status);

    if (result.status !== 'success') {
      const error = new Error('An error occurred while fetching the data.');
      throw error;
    }
    return result;
  };

  const {
    data,
    mutate,
    error: requestError,
    isLoading,
    isValidating,
  } = useSWR(fetchKey, () => fetcher(), {
    fallbackData: initialData,
    keepPreviousData: true,
    focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    shouldRetryOnError: true,
    errorRetryCount: 3,
    refreshInterval: status === 'complete' ? 0 : LONG_POLLING_INTERVAL,
  });

  const revalidate = () => {
    mutate();
  };

  return {
    isLoading,
    isValidating,
    error: requestError,
    data,
    revalidate,
  };
};

export default useRepositoryForkStatus;
